import * as React from 'react';
import * as _ from 'underscore';

import { SpaceHeaderTestIds } from '../Header/SpaceHeader';
import { TextAreaWidget } from '@tableau/widgets';
import { Messages, Formatters } from '../../../codegen/Localize';
import { DescriptorBlock, DescriptorBlockChildProps } from '../../../dataRole/components/definition-subspace/about-section/DescriptorBlock';
import { SpaceHeaderStyles } from '../Header/SpaceHeaderStyles';
import { CollectionSpaceHeaderProps } from './CollectionSpaceHeader';
import { CollectionsResourceQuery } from './Collections';
import { CollectionsErrorCode } from '../../../ts/helpers/ErrorCodes';

export const COLLECTION_NAME_MAX_LENGTH = 80;
export const EditableCollectionNameTestId: string = 'editable-collection-name';

function isValidName(name: string): boolean {
  return (!_.isUndefined(name) &&
    name.trim().length > 0 &&
    name.length <= COLLECTION_NAME_MAX_LENGTH);
}

export const getCollectionNameDisplay = (errorMessage: string, clearErrorMessage: () => void) =>
(props: DescriptorBlockChildProps<string>): JSX.Element => {
  const [isErrored, setIsErrored] = React.useState(undefined);

  const getMessageForNameTextArea = (value: string): string => {
    if (value.trim().length === 0) {
      !isErrored && setIsErrored(true);
      return Messages.createCollectionNameWhitespaceError();
    } else {
      isErrored && setIsErrored(undefined);
      const remainingChars = COLLECTION_NAME_MAX_LENGTH - value.length;
      return Messages.remainingCharacters({
        COUNT: remainingChars,
        COUNT_LOC: Formatters.formatCount(remainingChars)
      });
    }
  };

  const onNameChange = (onChangeFn: (newState: string) => void):
    (event: React.ChangeEvent<HTMLTextAreaElement>) => void => {
    return (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      errorMessage && clearErrorMessage();
      setMessage(getMessageForNameTextArea(value));
      onChangeFn(value);
    };
  };

  const [message, setMessage] = React.useState(errorMessage || getMessageForNameTextArea(props.value));

  if (!props.isEditing) {
    message && setMessage(undefined); // clear out message when done editing

    return <h1 data-tb-test-id={SpaceHeaderTestIds.name}
      className={SpaceHeaderStyles.CollectionTitle}>
      {props.value}
    </h1>;
  }

  // show character count message immediately upon entering edit mode
  !message && setMessage(errorMessage || getMessageForNameTextArea(props.value));

  return <TextAreaWidget
    testId='collection-name-edit'
    label={undefined}
    maxLength={COLLECTION_NAME_MAX_LENGTH}
    onChange={onNameChange(props.onChange)}
    defaultValue={props.value}
    className={SpaceHeaderStyles.ColumnFlexGrow}
    message={errorMessage || message}
    valid={(!!errorMessage || isErrored) ? false : undefined} // valid=undefined so textarea won't have green valid styling
    style={SpaceHeaderStyles.CollectionTitleEditTextBoxCSS}
  />;
};

export interface CollectionSpaceHeaderState {
  readonly errorMessage: string;
}

export class EditableCollectionNameComponent extends React.PureComponent<CollectionSpaceHeaderProps, CollectionSpaceHeaderState> {
  public constructor(props: CollectionSpaceHeaderProps) {
    super(props);
    this.state = { errorMessage: undefined };
  }

  public render(): JSX.Element {
    return <DescriptorBlock
      title={undefined}
      content={this.props.item.name}
      contentDisplay={getCollectionNameDisplay(this.state.errorMessage, this.clearErrorMessage)}
      editable={this.props.isAdminOrOwner}
      onSave={this.saveNameEdit(this.props.item.luid, this.updateErrorMessage)}
      keepEditModeOnSaveFail
      onCancel={this.clearErrorMessage}
      enableSaveButton={isValidName}
      data-tb-test-id={EditableCollectionNameTestId}
      testId={EditableCollectionNameTestId}
    />;
  }

  private updateErrorMessage = (newMessage: string): void => {
    this.setState({ errorMessage: newMessage });
  }

  private clearErrorMessage = (): void => {
    this.setState({ errorMessage: undefined });
  }

  private saveNameEdit = (luid: string, updateErrorMessage: (newMessage: string) => void) => {
    return async (newName: string): Promise<boolean> => {
      const updatedCollectionData = {
        name: newName
      };

      try {
        await (new CollectionsResourceQuery()).update(luid, updatedCollectionData);
        updateErrorMessage(undefined);
        return true;
      } catch (e) {
        let errorText = Messages.collectionRenameError({
          COLLECTION_NAME: newName
        });
        if (e.data && e.data.errorCode === CollectionsErrorCode.DUPLICATE_KEY) {
          errorText = Messages.collectionDuplicateRenameError();
        }

        updateErrorMessage(errorText);
        return false;
      }
    };
  }
}